import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { useConfigStore } from "@/stores/config";
import { getReportFooter, getReportHeader } from "@/reports/ReportUtil";
import { styles } from "@/reports/reportStyles";
import { CUSTOM_ICONS } from "@/constants/Unicode";
import { getFormattedDateStringNoTime } from "@/composables/dateField.js";
import { buildFutureSchedule } from "@/composables/futureSchedule";
import { sortEquipmentArray } from "@/composables/util";
import { INSPECTION_TYPE_PERFORMED, INSPECTION_COMMENTS } from "@/constants/Inspections";

const getAllEquipment = () => {
  const configStore = useConfigStore();
  const accessEquipment = [];
  const inspectionEquipment = [];
  const allEquips = configStore.getReferenceValues(REFERENCE_TABLE.INSPECTION_EQUIPMENT, null, null, true);
  for (const equipment of allEquips ?? []) {
    if (equipment.value.startsWith("A")) {
      accessEquipment.push(equipment);
    } else {
      inspectionEquipment.push(equipment);
    }
  }

  return [sortEquipmentArray(accessEquipment), sortEquipmentArray(inspectionEquipment)];
};

const getSelectedEquipment = (inspEvent) => {
  const selectedEquip = [];
  if (inspEvent) {
    for (let prop in inspEvent) {
      if (prop?.startsWith("equip") && inspEvent[prop] === "1") {
        selectedEquip.push(prop.replace("equip", ""));
      }
    }
  }
  return selectedEquip;
};

function setScheduleDocDefinition(structure) {
  const configStore = useConfigStore();

  const inspectionsPerformedDetails = [];
  inspectionsPerformedDetails.push(getInspectionPerformedHeader());
  getInspPerformedDetails(inspectionsPerformedDetails, structure);
  const futureData = getFutureData(structure);
  const underwaterDivers = configStore.getUnderwaterDivers;
  const tableData = getLegacyScheduleInformation(structure);

  let docDefinition = {
    header: function () {
      return getReportHeader("Schedule Page", structure);
    },
    footer: function (currentPage, pageCount) {
      return getReportFooter(currentPage, pageCount);
    },
    pageMargins: [20, 70, 20, 50],
    content: [
      { text: "", pageHeaderText: "Schedule Page" },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "SNBI Inspection Types Performed",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
                margin: [0, 3, 0, 2],
              },
            ],
          ],
        },
        layout: "noBorders",
      },
      {
        table: {
          headerRows: 0,
          widths: ["18%", "12%", "12%", "20%", "12%", "12%", "*"],
          body: inspectionsPerformedDetails,
          margin: [0, 5, 0, 5],
        },
      },
      {
        stack: [
          {
            table: {
              headerRows: 0,
              widths: ["100%"],
              body: [
                [
                  {
                    text: "SNBI Future Scheduling Information",
                    fillColor: "#ebebeb",
                    alignment: "center",
                    style: ["sectionHeader"],
                  },
                ],
              ],
            },
            layout: "noBorders",
            margin: [0, 5, 0, 5],
          },
          {
            table: {
              headerRows: 1,
              widths: ["20%", "20%", "20%", "20%", "*"],
              body: futureData,
            },
            layout: "noBorders",
          },
        ],
        unbreakable: true,
      },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Inspection Information",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 0],
      },
      {
        columns: [
          [
            {
              width: "70%",
              table: {
                headerRows: 0,
                widths: ["65%", "*"],
                body: [
                  [
                    {
                      text: "IE.05 Inspection Start Date: ",
                      bold: true,
                      margin: [0, 5, 0, 0],
                    },
                    {
                      text: getFormattedDateStringNoTime(structure.inspection.inspectionDate),
                      margin: [0, 5, 0, 0],
                    },
                  ],
                  [
                    {
                      text: "IE.06 Inspection End Date: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      text: getFormattedDateStringNoTime(structure.userInspection.overallEndDate),
                      margin: [0, 3, 0, 0],
                    },
                  ],
                  [
                    {
                      text: "IE.07 Team Leader: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      text: configStore
                        .getTeamLeaders(structure, false)
                        ?.find((t) => t.value === structure.inspection.inspectionUserId?.toString())?.title,

                      margin: [0, 3, 0, 0],
                    },
                  ],
                  [
                    {
                      text: "IE.08 Primary Inspection Type: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.INSPECTION_TYPE,
                        structure.inspection.inspectionType,
                      ),

                      margin: [0, 3, 0, 0],
                    },
                  ],
                  [
                    {
                      text: "IE.10 Inspection Performed By: ",
                      bold: true,
                      margin: [0, 5, 0, 0],
                    },
                    {
                      text: getInspPerformedBy(structure.inspection.inspectionControlId),
                      margin: [0, 5, 0, 0],
                    },
                  ],
                  [
                    {
                      text: "IE.11  Inspection Organization: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      text: structure.userInspection.inspOrgName,
                      margin: [0, 3, 0, 0],
                    },
                  ],
                  [
                    {
                      text: "IE.12 Inspection Team Member(s): ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      text: structure.userInspection.inspTeamHelper,
                      margin: [0, 3, 0, 0],
                    },
                  ],
                  [
                    {
                      text: "IE.13 Underwater Diver Name: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      text: underwaterDivers.find((t) => t.value === structure.userInspection.uwDiverName)?.title,
                      margin: [0, 3, 0, 0],
                    },
                  ],
                  [
                    {
                      text: "IE.14 Consultant Hired By: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.CONSULTANT_HIRED_BY,
                        structure.userInspection.consultantHiredBy,
                      ),
                      margin: [0, 3, 0, 0],
                    },
                  ],
                  [
                    {
                      text: "IE.15 Inspection Agreement Number: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      text: structure.userInspection.localInspContractNum,
                      margin: [0, 3, 0, 0],
                    },
                  ],
                  [
                    {
                      text: "IE.16 Utility Repair Indicator: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      text:
                        structure.userInspection.utilityRepair == "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
                      style: "customFontAwesome",
                      margin: [0, 3, 0, 0],
                    },
                  ],
                ],
              },
              layout: "noBorders",
            },
          ],
          [
            {
              table: {
                headerRows: 0,
                widths: ["75%", "*"],
                body: [
                  [
                    {
                      text: "IE.18  Non-NBIS Ext. Insp. Interval Elig.: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.EXTENDED_INSPECTION_INTERVAL_ELIGIBILITY,
                        structure.userInspection.extInspFreqElig,
                      ),
                      margin: [0, 3, 0, 0],
                    },
                  ],
                  [
                    {
                      text: "IE.19  Ext. Insp. Interval Conc.: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.INSPECTION_INTERVAL_CONCURRENCE,
                        structure.userInspection.extInspFreqConc,
                      ),
                      margin: [0, 3, 0, 0],
                    },
                  ],
                  [
                    {
                      text: "IE.20  NBIS Ext. Insp. Interval Elig.: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.METHOD1_ELIGIBILITY,
                        structure.userInspection.method1Elig,
                      ),
                      margin: [0, 3, 0, 0],
                    },
                  ],
                  [
                    {
                      width: "25%",
                      text: "IE.21 Weather: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      width: "25%",
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.WEATHER_CONDITION,
                        structure.userInspection.weatherCond,
                      ),
                      margin: [0, 3, 0, 0],
                    },
                  ],
                  [
                    {
                      width: "25%",
                      text: "IE.22 Temperature: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      width: "25%",
                      text: structure?.userInspection?.temp
                        ? Number.parseFloat(structure?.userInspection?.temp).toFixed(1)
                        : "",
                      margin: [0, 3, 0, 0],
                    },
                  ],
                  [
                    {
                      width: "25%",
                      text: "IE.23 NBI Crew Hours: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      width: "25%",
                      text: Number.isNaN(Number.parseFloat(structure?.userInspection?.crewHrs ?? ""))
                        ? ""
                        : Number.parseFloat(structure?.userInspection?.crewHrs ?? "0").toFixed(0),

                      margin: [0, 3, 0, 0],
                    },
                  ],
                  [
                    {
                      width: "25%",
                      text: "IE.24 Crane Hours: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      width: "25%",
                      text: Number.isNaN(Number.parseFloat(structure?.userInspection?.snooperHrs ?? ""))
                        ? ""
                        : Number.parseFloat(structure?.userInspection?.snooperHrs ?? "0").toFixed(0),
                      margin: [0, 3, 0, 0],
                    },
                  ],
                  [
                    {
                      width: "25%",
                      text: "IE.25 NSTM Hours: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      width: "25%",
                      text: Number.isNaN(Number.parseFloat(structure?.userInspection?.flaggerHrs ?? ""))
                        ? ""
                        : Number.parseFloat(structure?.userInspection?.flaggerHrs ?? "0").toFixed(0),
                      margin: [0, 3, 0, 0],
                    },
                  ],
                  [
                    {
                      width: "25%",
                      text: "IE.26 UW Hours: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      width: "25%",
                      text: Number.isNaN(Number.parseFloat(structure?.userInspection?.helperHrs ?? ""))
                        ? ""
                        : Number.parseFloat(structure?.userInspection?.helperHrs ?? "0").toFixed(0),
                      margin: [0, 3, 0, 0],
                    },
                  ],
                  [
                    {
                      width: "25%",
                      text: "IE.27 Engineering Costs: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      width: "25%",
                      text: structure.userInspection.fieldEngInspCost?.toLocaleString(),
                      margin: [0, 3, 0, 0],
                    },
                  ],
                  [
                    {
                      width: "25%",
                      text: "IE.28 Rigging Costs: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      width: "25%",
                      text: structure.userInspection.riggingInspCost?.toLocaleString(),
                      margin: [0, 3, 0, 0],
                    },
                  ],
                  [
                    {
                      width: "25%",
                      text: "IE.29 Office Costs: ",
                      bold: true,
                      margin: [0, 3, 0, 0],
                    },
                    {
                      width: "25%",
                      text: structure.userInspection.officeInspCost?.toLocaleString(),
                      margin: [0, 3, 0, 0],
                    },
                  ],
                ],
              },
              layout: "noBorders",
            },
          ],
        ],
      },
      {
        text: "Utility Repair Notes: ",
        bold: true,
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.UTILITY_REPAIR,
        )?.notes,
      },
      {
        columns: [
          [
            {
              width: "50%",
              table: {
                headerRows: 0,
                widths: ["65%", "*"],
                body: [
                  [
                    {
                      text: "IE.17 Inaccessible Area: ",
                      bold: true,
                      margin: [0, 5, 0, 0],
                    },
                    {
                      text: configStore.getReferenceValue(
                        REFERENCE_TABLE.INACCESSIBLE_AREA,
                        structure.userInspection.inAccessPortionStruct,
                      ),
                      margin: [0, 5, 0, 0],
                    },
                  ],
                ],
              },
              layout: "noBorders",
            },
          ],
          //We need an additional empty column here to force 6B49 to only use
          //half the page. If there's a better way of doing this, please do so
          [
            {
              table: {
                headerRows: 0,
                widths: ["50%", "*"],
                body: [
                  [
                    {
                      //Left empty intentionally. Any entry added here will be to
                      // the right of 6B49
                    },
                    {
                      //Left empty intentionally
                    },
                  ],
                ],
              },
              layout: "noBorders",
            },
          ],
        ],
      },
      {
        text: "Inaccessible Area Notes: ",
        bold: true,
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.INACCESSIBLE_AREA,
        )?.notes,
      },
      {
        stack: [
          {
            table: {
              headerRows: 0,
              widths: ["100%"],
              body: [
                [
                  {
                    text: "Next Inspection Information",
                    fillColor: "#ebebeb",
                    alignment: "center",
                    style: ["sectionHeader"],
                  },
                ],
              ],
            },
            layout: "noBorders",
            margin: [0, 5, 0, 0],
          },
          {
            columns: [
              [
                {
                  width: "50%",
                  table: {
                    headerRows: 0,
                    widths: ["65%", "*"],
                    body: [
                      [
                        {
                          text: "IE.30 Next Inspection Type: ",
                          bold: true,
                          margin: [0, 5, 0, 0],
                        },
                        {
                          text: configStore.getReferenceValue(
                            REFERENCE_TABLE.INSPECTION_TYPE,
                            structure.userInspection.nextInspType,
                          ),
                          margin: [0, 5, 0, 0],
                        },
                      ],
                      [
                        {
                          text: "IE.31 Next Access Equipment Date: ",
                          bold: true,
                          margin: [0, 5, 0, 0],
                        },
                        {
                          text: getFormattedDateStringNoTime(structure.userInspection.nextCraneInspDate),
                          margin: [0, 5, 0, 0],
                        },
                      ],
                    ],
                  },
                  layout: "noBorders",
                },
              ],
              [
                {
                  table: {
                    headerRows: 0,
                    widths: ["50%", "*"],
                    body: [
                      [
                        {
                          text: "IE.32 Next Inspection Performed By: ",
                          bold: true,
                          margin: [0, 5, 0, 0],
                        },
                        {
                          text: configStore.getReferenceValue(
                            REFERENCE_TABLE.NEXT_INSPECTION_BY,
                            structure.structureBridge.bridgeGroup,
                          ),
                          margin: [0, 5, 0, 0],
                        },
                      ],
                      [
                        {
                          text: "IE.33 Next Team Leader: ",
                          bold: true,
                          margin: [0, 5, 0, 0],
                        },
                        {
                          text: configStore
                            .getTeamLeaders(structure, false)
                            ?.find((t) => t.value === structure.structureBridge.nextInspId?.toString())?.title,
                          margin: [0, 5, 0, 0],
                        },
                      ],
                    ],
                  },
                  layout: "noBorders",
                },
              ],
            ],
          },
        ],
        unbreakable: true,
      },
      {
        text: "Future Inspection Limited Scope Notes: ",
        bold: true,
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.FUTURE_INSPECTION_LIMITED_SCOPE,
        )?.notes,
      },
      {
        stack: [
          {
            table: {
              headerRows: 0,
              widths: ["100%"],
              body: [
                [
                  {
                    text: "Legacy Schedule Information",
                    fillColor: "#ebebeb",
                    alignment: "center",
                    style: ["sectionHeader"],
                  },
                ],
              ],
            },
            layout: "noBorders",
            margin: [0, 5, 0, 0],
          },
          {
            table: {
              headerRows: 1,
              widths: ["20%", "16%", "16%", "16%", "16%", "16%"],
              body: tableData,
            },
            layout: "noBorders",
            margin: [0, 5, 0, 0],
          },
        ],
        unbreakable: true,
      },
    ],
    styles: styles,
    defaultStyle: {
      fontSize: 10,
    },
  };
  return docDefinition;
}

function getInspectionPerformedHeader() {
  return [
    {
      text: "B.IE.01\nInspection\nType",
      style: ["tableHeader"],
    },
    {
      text: "B.IE.02\nInspection\nStart Date",
      style: ["tableHeader"],
    },
    {
      text: "B.IE.03\nInspection\nEnd Date",
      style: ["tableHeader"],
    },
    {
      text: "B.IE.04\nNCBI\n(Team Leader)",
      style: ["tableHeader"],
    },
    {
      text: "B.IE.05\nInspection\nInterval",
      style: ["tableHeader"],
    },
    {
      text: "B.IE.06\nInspection\nDue Date",
      style: ["tableHeader"],
    },
    {
      text: "B.IE.07\nRBI\nMethod",
      style: ["tableHeader"],
    },
  ];
}

function getFutureScheduleHeader() {
  return [
    {
      text: "Inspection Type",
      style: ["tableHeader"],
    },
    {
      text: "IE.01 Required",
      style: ["tableHeader"],
    },
    {
      text: "IE.02 Last Date",
      style: ["tableHeader"],
    },
    {
      text: "IE.03 Interval",
      style: ["tableHeader"],
    },
    {
      text: "IE.04 Next Inspection Date",
      style: ["tableHeader"],
    },
  ];
}

function getInspPerformedDetails(inspectionsPerformedDetails, structure) {
  const configStore = useConfigStore();
  if (structure?.snbiInspEvents) {
    structure?.snbiInspEvents?.sort((a, b) => a.inspType?.localeCompare(b.inspType));
  }
  for (const inspEvent of structure?.snbiInspEvents || []) {
    inspectionsPerformedDetails.push(
      [
        configStore.getReferenceValue(REFERENCE_TABLE.SNBI_INSPECTION_TYPE, inspEvent.inspType),
        getFormattedDateStringNoTime(inspEvent?.startDate),
        getFormattedDateStringNoTime(inspEvent?.endDate),
        configStore
          .getTeamLeaders(structure, inspEvent?.inspType == INSPECTION_TYPE_PERFORMED.NSTM)
          ?.find((t) => t?.value === inspEvent?.teamLeader?.toString())?.title,
        inspEvent?.inspInterval,
        getFormattedDateStringNoTime(inspEvent?.dueDate),
        configStore.getReferenceValue(REFERENCE_TABLE.RBI_METHOD, inspEvent?.rbm),
      ].map((d) => {
        return {
          text: d,
          alignment: "center",
        };
      }),
    );
    getInspPerformedDetailExpanded(inspectionsPerformedDetails, inspEvent, structure);
  }
}

function createEquipmentCheckbox(cell, selectedEquip) {
  return {
    text: [
      {
        text: selectedEquip?.includes(cell.value) ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
        style: "customFontAwesome",
      },
      { text: " " + cell?.title },
    ],
  };
}

function buildEquipmentRows(equipment, selectedEquip, equipmentPerRow) {
  const equipRows = [];
  const chunkedArr = splitArrayIntoChunks(equipment, equipmentPerRow);

  for (const row of chunkedArr ?? []) {
    let equipRow = [];

    for (const cell of row ?? []) {
      equipRow = equipRow?.concat([createEquipmentCheckbox(cell, selectedEquip)]);
    }

    // Pad row with empty cells if needed for pdfmake
    if (row?.length < equipmentPerRow) {
      for (let i = 0; i < equipmentPerRow - row?.length; i++) {
        equipRow = equipRow?.concat([{}]);
      }
    }

    equipRows.push(equipRow);
  }

  return equipRows;
}

function createEquipmentSection(title, equipmentRows, widths = ["18%", "18%", "17%", "17%", "17%", "*"]) {
  return {
    stack: [
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: title,
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["subSectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
      },
      {
        table: {
          headerRows: 0,
          widths: widths,
          body: equipmentRows,
        },
        layout:
          widths.length === 6
            ? {
                hLineWidth: () => 0,
                vLineWidth: () => 0,
                paddingLeft: () => 0,
                paddingRight: () => 0,
                paddingTop: () => 0,
                paddingBottom: () => 0,
              }
            : "noBorders",
        fontSize: 9,
      },
    ],
    unbreakable: true,
  };
}

function createInspectionDetailsColumns(inspEvent, structure, configStore) {
  return {
    columns: [
      {
        table: {
          body: [
            [
              {
                text: "B.IE.01 Inspection Type: ",
                bold: true,
                margin: [0, 3, 0, 0],
              },
              {
                text: configStore.getReferenceValue(REFERENCE_TABLE.SNBI_INSPECTION_TYPE, inspEvent?.inspType),
                margin: [0, 3, 0, 0],
              },
            ],
            [
              {
                text: "B.IE.02 Inspection Start Date: ",
                bold: true,
                margin: [0, 3, 0, 0],
              },
              {
                text: getFormattedDateStringNoTime(inspEvent?.startDate),
                margin: [0, 3, 0, 0],
              },
            ],
            [
              {
                text: "B.IE.03 Inspection End Date: ",
                bold: true,
                margin: [0, 3, 0, 0],
              },
              {
                text: getFormattedDateStringNoTime(inspEvent?.endDate),
                margin: [0, 3, 0, 0],
              },
            ],
            [
              {
                text: "B.IE.04 NCBI (Team Leader): ",
                bold: true,
                margin: [0, 3, 0, 0],
              },
              {
                text: configStore
                  .getTeamLeaders(structure, inspEvent?.inspType == INSPECTION_TYPE_PERFORMED.NSTM)
                  ?.find((t) => t.value === inspEvent?.teamLeader?.toString())?.title,
                margin: [0, 3, 0, 0],
              },
            ],
            [
              {
                text: "B.IE.05 Inspection Interval: ",
                bold: true,
                margin: [0, 3, 0, 0],
              },
              {
                text: [inspEvent?.inspInterval, " mos"],
                margin: [0, 3, 0, 0],
              },
            ],
          ],
        },
        layout: "noBorders",
      },
      {
        table: {
          body: [
            [
              {
                text: "B.IE.06 Inspection Due Date: ",
                bold: true,
                margin: [0, 3, 0, 0],
              },
              {
                text: getFormattedDateStringNoTime(inspEvent?.dueDate),
                margin: [0, 3, 0, 0],
              },
            ],
            [
              {
                text: "B.IE.07 RBI Method: ",
                bold: true,
                margin: [0, 3, 0, 0],
              },
              {
                text: configStore.getReferenceValue(REFERENCE_TABLE.RBI_METHOD, inspEvent?.rbm),
                margin: [0, 3, 0, 0],
              },
            ],
            [
              {
                text: "B.IE.08 Quality Control Date: ",
                bold: true,
                margin: [0, 3, 0, 0],
              },
              {
                text: getFormattedDateStringNoTime(inspEvent?.qcDate),
                margin: [0, 3, 0, 0],
              },
            ],
            [
              {
                text: "B.IE.10 Modified Date: ",
                bold: true,
                margin: [0, 3, 0, 0],
              },
              {
                text: new Date(inspEvent?.inspectionDataUpdateDateTime)?.toLocaleString(),
                margin: [0, 3, 0, 0],
              },
            ],
          ],
        },
        layout: "noBorders",
      },
    ],
  };
}

function getInspPerformedDetailExpanded(inspectionsPerformedDetails, inspEvent, structure) {
  const configStore = useConfigStore();
  const [accessEquipment, inspectionEquipment] = getAllEquipment();
  const selectedEquip = getSelectedEquipment(inspEvent);
  const equipmentPerRow = 6;

  const acessEquip = buildEquipmentRows(accessEquipment, selectedEquip, equipmentPerRow);
  const inspEquip = buildEquipmentRows(inspectionEquipment, selectedEquip, equipmentPerRow);

  inspectionsPerformedDetails.push([
    {
      colSpan: 7,
      stack: [
        createInspectionDetailsColumns(inspEvent, structure, configStore),
        {
          colSpan: 7,
          columns: [
            {
              text: "B.IE.11 Limited Scope Descr: ",
              bold: true,
              margin: [0, 3, 0, 0],
              width: "25%",
            },
            {
              text: inspEvent?.scopeNote,
              margin: [3, 3, 0, 0],
              width: "*",
            },
          ],
        },
        {
          colSpan: 7,
          columns: [
            {
              text: "B.IE.12 Inspection Equipment: ",
              bold: true,
              margin: [0, 3, 0, 0],
              width: "25%",
            },
            {
              text: selectedEquip?.join("|"),
              margin: [3, 3, 0, 0],
              width: "*",
            },
          ],
        },
        createEquipmentSection(
          "Access Equipment (Check all that apply, at least one is required)",
          acessEquip,
          undefined,
        ),
        createEquipmentSection("Inspection Equipment (Check all that apply, at least one is required)", inspEquip),
      ],
    },
  ]);
}

const getFutureData = (structure) => {
  const configStore = useConfigStore();

  const futureScheduleData = buildFutureSchedule(structure);
  const futureSchedule = [];
  futureSchedule.push(getFutureScheduleHeader());

  for (const a of futureScheduleData ?? []) {
    const checkbox = a?.is7A57RequiredValue == "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE;
    futureSchedule.push([
      {
        text: configStore.getReferenceValue(REFERENCE_TABLE.SNBI_INSPECTION_TYPE, a?.inspType),
        style: ["tableBody"],
      },
      {
        text: a?.show7A57Checkbox ? checkbox : "N/A",
        style: a?.show7A57Checkbox ? ["customFontAwesome", "tableBody"] : ["tableBody"],
      },
      {
        text: getFormattedDateStringNoTime(a?.lastInspectionDate),
        style: ["tableBody"],
      },
      { text: `${a?.inspectionInterval || 0} mos`, style: ["tableBody"] },
      {
        text: a?.showInspDueDate ? getFormattedDateStringNoTime(a?.inspectionDueDate) : "N/A",
        style: ["tableBody"],
      },
    ]);
  }
  return futureSchedule;
};

const getInspPerformedBy = (id) => {
  const configStore = useConfigStore();
  return configStore
    .getReferenceValues(REFERENCE_TABLE.INSPECTION_PERFORMED_BY, null, null, true)
    ?.find((selectItem) => selectItem?.value == id)?.title;
};

const getLegacyScheduleInformation = (structure) => {
  const tableData = [];
  tableData.push(
    [
      {
        text: "",
        style: ["tableHeader"],
      },
      {
        text: "7A06\nPerformed",
        style: ["tableHeader"],
      },
      {
        text: "7A07\nRequired",
        style: ["tableHeader"],
      },
      {
        text: "7A08\nLast Inspection\nDate",
        style: ["tableHeader"],
      },
      {
        text: "7A09\nInspection\nInterval",
        style: ["tableHeader"],
      },
      {
        text: "7A10\nNext Inspection\nDate",
        style: ["tableHeader"],
      },
    ],
    [
      {
        text: "NBI:",
        bold: true,
        alignment: "center",
      },
      {
        text: structure?.inspection?.nbInspectionDone == "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
        style: "customFontAwesome",
        alignment: "center",
      },
      {
        text: "",
        alignment: "center",
      },
      {
        text: getFormattedDateStringNoTime(structure?.inspection?.lastInspection),
        alignment: "center",
      },
      { text: structure?.inspection?.brInspectionFreq + " mos", alignment: "center" },
      {
        text: getFormattedDateStringNoTime(structure?.inspection?.nextInspection),
        alignment: "center",
      },
    ],
    [
      {
        text: "NSTM:",
        bold: true,
        alignment: "center",
      },
      {
        text: structure?.inspection?.fcInspectionDone == "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
        style: "customFontAwesome",
        alignment: "center",
      },
      {
        text: structure?.inspection?.fcInspectionReq == "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
        style: "customFontAwesome",
        alignment: "center",
      },
      {
        text: getFormattedDateStringNoTime(structure?.inspection?.fcLastInspection),
        alignment: "center",
      },
      { text: structure?.inspection?.fcInspectionFreq + " mos", alignment: "center" },
      {
        text: getFormattedDateStringNoTime(structure?.inspection?.fcNextDate),
        alignment: "center",
      },
    ],
    [
      {
        text: "Underwater:",
        bold: true,
        alignment: "center",
      },
      {
        text: structure?.inspection?.uwInspectionDone == "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
        style: "customFontAwesome",
        alignment: "center",
      },
      {
        text: structure?.inspection?.uwInspectionReq == "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
        style: "customFontAwesome",
        alignment: "center",
      },
      {
        text: getFormattedDateStringNoTime(structure?.inspection?.uwLastInspection),
        alignment: "center",
      },
      { text: structure?.inspection?.uwInspectionFreq + " mos", alignment: "center" },
      {
        text: getFormattedDateStringNoTime(structure?.inspection?.uwNextDate),
        alignment: "center",
      },
    ],
    [
      {
        text: "Other Special:",
        bold: true,
        alignment: "center",
      },
      {
        text: structure?.inspection?.osInspectionDone == "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
        style: "customFontAwesome",
        alignment: "center",
      },
      {
        text: structure?.inspection?.osInspectionReq == "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
        style: "customFontAwesome",
        alignment: "center",
      },
      {
        text: getFormattedDateStringNoTime(structure?.inspection?.osLastInspection),
        alignment: "center",
      },
      { text: structure?.inspection?.osInspectionFreq + " mos", alignment: "center" },
      {
        text: getFormattedDateStringNoTime(structure?.inspection?.osNextDate),
        alignment: "center",
      },
    ],
    [
      {
        text: "Element:",
        bold: true,
        alignment: "center",
      },
      {
        text: structure?.inspection?.elInspectionDone == "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
        style: "customFontAwesome",
        alignment: "center",
      },
      {
        text: structure?.inspection?.elInspectionReq == "1" ? CUSTOM_ICONS.SQUARE_CHECK : CUSTOM_ICONS.SQUARE,
        style: "customFontAwesome",
        alignment: "center",
      },
      {
        text: getFormattedDateStringNoTime(structure?.inspection?.elLastInsp),
        alignment: "center",
      },
      { text: structure?.inspection?.elInspectionFreq + " mos", alignment: "center" },
      {
        text: getFormattedDateStringNoTime(structure?.inspection?.elNextDate),
        alignment: "center",
      },
    ],
  );
  return tableData;
};

function splitArrayIntoChunks(array, chunkSize) {
  const chunkedArray = [];

  for (let i = 0; i < array?.length; i += chunkSize) {
    const chunk = array.slice(i, i + chunkSize);
    chunkedArray.push(chunk);
  }
  return chunkedArray;
}

export { setScheduleDocDefinition };
